import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, message } from 'antd';
import { authApi } from '../../services/api';

interface Props {
    open: boolean;
    onCancel: () => void;
}

interface FormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangePasswordModal: React.FC<Props> = ({ open, onCancel }) => {
    const [form] = Form.useForm<FormValues>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            form.resetFields();
        }
    }, [form, open]);

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            await authApi.changePassword({
                old_password: values.oldPassword,
                new_password: values.newPassword,
            });

            message.success('密码修改成功');
            form.resetFields();
            onCancel();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }

            if (error.response?.data?.detail) {
                message.error(error.response.data.detail);
            } else {
                message.error('密码修改失败');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="修改密码"
            open={open}
            onCancel={handleCancel}
            onOk={handleOk}
            confirmLoading={loading}
            destroyOnHidden
            okText="保存"
            cancelText="取消"
        >
            <Form<FormValues> form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                    label="旧密码"
                    name="oldPassword"
                    rules={[
                        { required: true, message: '请输入旧密码' },
                        { min: 8, message: '旧密码至少 8 位' },
                    ]}
                >
                    <Input.Password placeholder="请输入旧密码" />
                </Form.Item>

                <Form.Item
                    label="新密码"
                    name="newPassword"
                    dependencies={['oldPassword']}
                    rules={[
                        { required: true, message: '请输入新密码' },
                        { min: 8, message: '新密码至少 8 位' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || value !== getFieldValue('oldPassword')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('新密码不能与旧密码相同'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="请输入新密码" />
                </Form.Item>

                <Form.Item
                    label="确认新密码"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                        { required: true, message: '请再次输入新密码' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || value === getFieldValue('newPassword')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('两次输入的新密码不一致'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="请再次输入新密码" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ChangePasswordModal;
